/**
 * Token Encryption Utility
 * Encrypts JWT tokens before storing in localStorage using Web Crypto API
 * Binds encryption to device fingerprint for additional security
 */

const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recommended for AES-GCM

/**
 * Generate a device fingerprint
 * Combines browser/device characteristics to create unique identifier
 * Uses only stable characteristics to avoid false positives
 * @returns {string} Device fingerprint
 */
export const getDeviceFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    // Removed: screen.width + 'x' + screen.height (changes on window resize/external monitor)
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform
  ];

  // Create stable hash of components
  return components.join('|');
};

/**
 * Derive encryption key from device fingerprint
 * Uses PBKDF2 to derive a cryptographic key
 * @param {string} fingerprint - Device fingerprint
 * @returns {Promise<CryptoKey>} Derived encryption key
 */
const deriveKey = async (fingerprint) => {
  const encoder = new TextEncoder();
  const fingerprintData = encoder.encode(fingerprint);

  // Import fingerprint as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    fingerprintData,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  // Derive AES-GCM key
  // Note: Using fingerprint as salt is not ideal but acceptable for device-binding
  // In Phase 2, we'll move to HttpOnly cookies which eliminates this entirely
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: fingerprintData, // Using fingerprint as salt
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt token data
 * @param {Object} tokenData - Token object to encrypt
 * @returns {Promise<string>} Base64-encoded encrypted data with IV
 */
export const encryptTokens = async (tokenData) => {
  try {
    const fingerprint = getDeviceFingerprint();
    const key = await deriveKey(fingerprint);

    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Encrypt token data
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(tokenData));

    const encryptedData = await crypto.subtle.encrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: iv
      },
      key,
      data
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedData), iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Token encryption failed:', error);
    throw new Error('Failed to encrypt tokens');
  }
};

/**
 * Decrypt token data
 * @param {string} encryptedData - Base64-encoded encrypted data
 * @returns {Promise<Object>} Decrypted token object
 */
export const decryptTokens = async (encryptedData) => {
  try {
    const fingerprint = getDeviceFingerprint();
    const key = await deriveKey(fingerprint);

    // Decode base64
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const encrypted = combined.slice(IV_LENGTH);

    // Decrypt
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: iv
      },
      key,
      encrypted
    );

    // Parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedData);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Token decryption failed:', error);
    // Decryption failure could indicate:
    // 1. Device fingerprint changed (different browser/device)
    // 2. Data corruption
    // 3. Tampering attempt
    throw new Error('Failed to decrypt tokens - device mismatch or corrupted data');
  }
};

/**
 * Check if Web Crypto API is available
 * @returns {boolean} True if encryption is supported
 */
export const isEncryptionSupported = () => {
  return typeof crypto !== 'undefined' &&
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.subtle.encrypt === 'function';
};
